<template>
  <div>
    <v-dialog v-model="reminder" max-width="480" :persistent="blocked" content-class="campus-renewal-dialog renewal-reminder" aria-labelledby="cr-reminder-title">
      <v-card v-if="current" class="cr-shell">
        <v-card-title class="cr-header">
          <span class="cr-symbol"><v-icon aria-hidden="true">mdi-calendar-clock-outline</v-icon></span>
          <div class="cr-heading"><h2 id="cr-reminder-title">{{ current.stage === 'expired' ? '校区已过期' : '校区续费提醒' }}</h2><p>及时续费，让教学管理持续在线</p></div>
        </v-card-title>
        <v-card-text class="cr-body cr-reminder-body">
          <div class="cr-reminder-campus"><v-icon size="18" aria-hidden="true">mdi-map-marker-outline</v-icon><strong>{{ current.shopName }}</strong></div>
          <p class="cr-reminder-message">{{ reminderText }}，有效期至 <strong>{{ current.expireDate }}</strong>。</p>
          <div class="cr-reminder-offer"><div><span class="cr-eyebrow">当前版本续费</span><span class="cr-muted">版本权益与原有数据保留</span></div><div class="cr-mini-price">¥{{ money(amountCents) }}<span> / 年</span></div></div>
        </v-card-text>
        <v-card-actions class="cr-footer cr-reminder-actions">
          <v-btn text class="cr-secondary" @click="dismissReminder">{{ blocked ? '查看续费页面' : '稍后提醒' }}</v-btn>
          <v-spacer />
          <v-btn depressed color="#d9363e" dark class="cr-primary" @click="dismissReminder(); open()">立即续费<v-icon right size="18" aria-hidden="true">mdi-arrow-right</v-icon></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card v-if="!ready || blocked" class="ma-6 pa-6" outlined>
      <v-card-title>{{ !ready ? '正在确认校区有效期' : '当前校区已过期' }}</v-card-title>
      <v-card-text>{{ error || '续费后即可继续使用，原有数据和版本权益保留。您也可以切换至其他有效校区。' }}</v-card-text>
      <v-card-actions>
        <v-btn v-if="ready" color="primary" @click="open()">校区续费</v-btn>
        <v-btn text :loading="loading" @click="refresh">刷新状态</v-btn>
        <v-btn text @click="$emit('logout')">退出登录</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialog" max-width="720" scrollable content-class="campus-renewal-dialog renewal-overview" aria-labelledby="cr-overview-title">
      <v-card class="cr-shell">
        <v-card-title class="cr-header">
          <span class="cr-symbol"><v-icon aria-hidden="true">mdi-school-outline</v-icon></span>
          <div class="cr-heading"><h2 id="cr-overview-title">校区续费</h2><p>延续当前版本，安心管理每一堂课</p></div>
          <v-spacer /><v-btn icon class="cr-close" aria-label="关闭" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="cr-body">
          <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
          <v-select v-model="selectedID" :items="shops" item-text="shopName" item-value="id" label="续费校区" outlined dense hide-details color="#d9363e" class="cr-campus-select" :disabled="saving" @change="clearOrder" />
          <template v-if="selected">
            <div class="cr-campus-card">
              <div class="cr-campus-title"><strong>{{ selected.shopName }}</strong><span class="cr-version">{{ versionName(selected.vipLevel) }}</span></div>
              <div class="cr-campus-expiry"><span>当前有效期至：<strong>{{ selected.expireDate }}</strong></span><span class="cr-status" :class="{ 'cr-status-expired': selected.daysRemaining < 0 }">{{ selected.daysRemaining < 0 ? '已过期' : selected.daysRemaining === 0 ? '今日到期' : `剩余 ${selected.daysRemaining} 天` }}</span></div>
              <p v-if="selected.daysRemaining < 0" class="cr-expired-note">校区已过期，续费后可继续使用。</p>
            </div>
            <v-alert v-if="selected.issue" type="warning" text class="mt-4">{{ selected.issue }}</v-alert>
            <div class="cr-section-heading"><h3>续费方案</h3><v-btn text small class="cr-secondary" :loading="historyLoading" @click="loadOrders"><v-icon left size="16" aria-hidden="true">mdi-refresh</v-icon>刷新订单</v-btn></div>
            <v-progress-linear v-if="historyLoading && !historyLoaded" indeterminate color="#d9363e" class="mb-4" />
            <v-alert v-if="historyError" type="error" text>{{ historyError }}，请刷新订单后重试。</v-alert>
            <template v-if="historyLoaded && !historyError">
              <p v-if="pendingOrders.length" class="cr-pending-hint"><v-icon size="17" aria-hidden="true">mdi-information-outline</v-icon>已有待支付续费单，可直接继续支付</p>
              <div v-for="item in pendingOrders" :key="item.orderNo" class="cr-pending-card">
                <div class="cr-pending-main"><span class="cr-status cr-status-pending">{{ stateName(item) }}</span><h4>{{ versionName(item.vipLevel) }} · 续费 1 年</h4><div class="cr-order-number">订单号：{{ item.orderNo }}</div><span class="cr-muted">微信支付</span></div>
                <div class="cr-pending-action"><strong class="cr-pending-price">¥{{ money(item.amountCents) }}</strong><v-btn depressed color="#d9363e" :dark="paymentEnabled && ready && !selected.issue" class="cr-primary" :loading="saving" :disabled="!paymentEnabled || !ready || !!selected.issue" @click="openOrder(item)">继续支付</v-btn></div>
              </div>
              <v-alert v-if="reviewOrders.length" type="warning" text>有续费订单待客服核实，请先联系客服确认付款结果，暂勿重复付款。</v-alert>
              <div v-if="!pendingOrders.length && !reviewOrders.length" class="cr-plan">
                <div class="cr-plan-top"><div><span class="cr-plan-label">年度续费</span><h4>当前版本 · 续费 1 年</h4></div><span class="cr-offer-tag">续费优惠价</span></div>
                <div class="cr-plan-pricing"><div class="cr-price"><span class="cr-currency">¥</span><strong>{{ money(amountCents) }}</strong><span class="cr-price-unit">/ 校区 / 年</span></div><del v-if="originalCents > amountCents" class="cr-original-price">原价 ¥{{ money(originalCents) }}</del></div>
                <div class="cr-benefits"><span><v-icon size="16" aria-hidden="true">mdi-check-circle-outline</v-icon>版本权益保留</span><span><v-icon size="16" aria-hidden="true">mdi-check-circle-outline</v-icon>原有数据保留</span></div>
                <div class="cr-plan-bottom"><span>保持 {{ versionName(selected.vipLevel) }}，续享 1 年服务</span><v-btn depressed color="#d9363e" :dark="!selected.issue && ready && !historyLoading" class="cr-primary" :loading="saving" :disabled="!!selected.issue || !ready || historyLoading" @click="createOrder">我要续费<v-icon right size="18" aria-hidden="true">mdi-arrow-right</v-icon></v-btn></div>
              </div>
            </template>
            <div class="cr-rule"><v-icon size="17" aria-hidden="true">mdi-calendar-check-outline</v-icon><span>未过期从原到期日顺延；已过期从付款当天开始。</span></div>
            <v-alert v-if="!paymentEnabled" type="info" text dense class="cr-offline-note">在线支付暂未开放。您可以先保存续费订单，或联系客服办理续费。保存订单不会扣款或延长有效期。</v-alert>
            <v-expansion-panels v-if="otherOrders.length" flat class="cr-history">
              <v-expansion-panel><v-expansion-panel-header>历史续费订单（{{ otherOrders.length }}）</v-expansion-panel-header><v-expansion-panel-content>
                <v-list dense>
                  <v-list-item v-for="item in otherOrders" :key="item.orderNo" class="cr-history-item">
                    <v-list-item-content>
                      <v-list-item-title>¥{{ money(item.amountCents) }} · {{ stateName(item) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.orderNo }}</v-list-item-subtitle>
                      <v-list-item-subtitle v-if="item.state === 'paid'">已续费至 {{ item.renewTo }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action><v-btn small text class="cr-secondary" :disabled="saving" @click="openOrder(item)">查看</v-btn></v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content></v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-card-text>
        <v-card-actions class="cr-footer"><v-icon size="18" aria-hidden="true">mdi-headset</v-icon><span>续费遇到问题？</span><span class="cr-contact">客服微信：15674980303</span></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="paymentDialog" max-width="600" scrollable :persistent="saving" content-class="campus-renewal-dialog renewal-payment" aria-labelledby="cr-payment-title">
      <v-card class="cr-shell">
        <v-card-title class="cr-header">
          <span class="cr-symbol cr-symbol-payment"><v-icon aria-hidden="true">mdi-shield-check-outline</v-icon></span>
          <div class="cr-heading"><h2 id="cr-payment-title">校区续费付款</h2><p>微信扫码支付，完成后自动续费</p></div>
          <v-spacer /><v-btn icon class="cr-close" aria-label="关闭付款" :disabled="saving" @click="paymentDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="cr-body cr-payment-body">
          <v-alert v-if="paymentError" type="error" text>{{ paymentError }}</v-alert>
          <div v-if="saving && !order" class="text-center pa-6"><v-progress-circular indeterminate color="primary" /><p class="mt-4">正在生成续费订单，请稍候…</p></div>
          <template v-if="order">
            <div class="cr-payment-summary">
              <div class="cr-payment-campus">{{ order.shopName }}<span class="cr-version">{{ versionName(order.vipLevel) }}</span></div>
              <div class="cr-payment-plan">当前版本 · 续费 1 年</div>
              <div class="cr-payment-amount"><span class="text-h4">¥{{ money(order.amountCents) }}</span><span class="cr-price-unit">/ 校区 / 年</span></div>
              <div class="cr-payment-expiry">{{ order.state === 'paid' ? '本次续费后有效期至' : '预计续费后有效期至' }}：<strong>{{ order.state === 'paid' ? order.renewTo : (selected && selected.renewTo || '待核实') }}</strong></div>
            </div>
            <div class="cr-payment-detail">
              <div class="cr-payment-status" role="status"><span class="cr-status" :class="{ 'cr-status-expired': order.state === 'review', 'cr-status-paid': order.state === 'paid' }">{{ stateName(order) }}</span></div>
              <div class="cr-order-number">订单号：{{ order.orderNo }}</div>
              <div v-if="order.amountCents !== amountCents" class="cr-payment-caption">应付 ¥{{ money(order.amountCents) }}，保持 {{ versionName(order.vipLevel) }}。</div>
              <div v-if="order.state === 'pending' && order.qrImage && !orderExpired" class="cr-qr-area">
                <div class="cr-qr-frame"><img :src="order.qrImage" alt="微信支付二维码" width="240" height="240" /></div>
                <p class="cr-scan-instruction"><v-icon size="20" aria-hidden="true">mdi-wechat</v-icon>请使用微信扫一扫支付</p>
                <p class="cr-countdown">二维码剩余 <strong>{{ countdown }}</strong>，付款后将自动续费 1 年。</p>
              </div>
              <v-alert v-if="order.state === 'creating'" type="info" text class="mt-3">正在确认支付订单，请稍候或刷新支付状态。重复操作会继续使用同一订单。</v-alert>
              <v-alert v-if="order.state === 'draft'" type="info" text class="mt-3">订单已保存，尚未付款。</v-alert>
              <v-alert v-if="order.state === 'repriced'" type="info" text class="mt-3">订单价格已调整，原二维码已失效，正在读取新付款订单。</v-alert>
              <v-alert v-if="order.state === 'paid'" type="success" text class="mt-3">支付成功，已续费至 {{ order.renewTo }}。</v-alert>
              <v-alert v-if="order.state === 'review'" type="warning" text class="mt-3">支付信息需要客服核实，请提供订单号，暂勿重复付款。</v-alert>
              <v-alert v-if="order.state === 'closed' || (orderExpired && ['draft', 'creating', 'pending'].includes(order.state))" type="info" text class="mt-3">二维码已到期。若已付款，请先刷新支付状态；未付款可返回校区续费重新下单。</v-alert>
            </div>
            <p class="cr-payment-rule">未过期从原到期日顺延；已过期从付款当天开始。以付款时的校区有效期为准。</p>
          </template>
        </v-card-text>
        <v-card-actions class="cr-footer cr-payment-footer">
          <span class="cr-contact">客服微信：15674980303</span><v-spacer />
          <v-btn text class="cr-secondary" :disabled="saving" @click="paymentDialog = false">返回校区续费</v-btn>
          <v-btn v-if="order && ['creating', 'pending', 'closed', 'repriced'].includes(order.state)" text class="cr-secondary" :loading="polling" @click="pollOrder"><v-icon left size="17" aria-hidden="true">mdi-refresh</v-icon>刷新支付状态</v-btn>
          <v-btn v-if="order && order.state === 'paid'" depressed color="#d9363e" dark class="cr-primary" @click="finishPayment">完成并刷新页面</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import global from '../config/global'
import storage from '../config/storage'
import { EventBus } from '../config/eventBus'
export default {
  name: 'CampusRenewal',
  props: { shopId: { default: '' }, employeeId: { default: '' } },
  data: () => ({ shops: [], ready: false, loading: false, error: '', dialog: false, reminder: false, paymentDialog: false, paymentError: '',
    selectedID: null, channel: 'wechat', order: null, orders: [], saving: false,
    historyLoading: false, historyLoaded: false, historyGeneration: 0, historyError: '', amountCents: 100000, originalCents: 128000,
    channels: [], polling: false, pollTimer: null, clockTimer: null, clock: Date.now(), paymentGeneration: 0, completedOrder: null,
    dismissed: {}, timer: null, disposed: false }),
  computed: {
    selectedOrders() { return this.orders.filter(o => String(o.shopId) === String(this.selectedID)) },
    pendingOrders() { return this.selectedOrders.filter(o => ['draft', 'creating', 'pending'].includes(o.state) && new Date(o.expiresAt).getTime() > this.clock) },
    reviewOrders() { return this.selectedOrders.filter(o => o.state === 'review') },
    otherOrders() { return this.selectedOrders.filter(o => !this.pendingOrders.some(p => p.orderNo === o.orderNo)) },
    paymentEnabled() { return this.channels.some(c => c.code === this.channel && c.enabled) },
    orderExpired() { return this.order && new Date(this.order.expiresAt).getTime() <= this.clock },
    countdown() { const seconds = Math.max(0, Math.ceil((new Date(this.order.expiresAt).getTime() - this.clock) / 1000)); return `${Math.floor(seconds / 60)} 分 ${seconds % 60} 秒` },
    current() { return this.shopId ? this.shops.find(s => String(s.id) === String(this.shopId)) : this.shops[0] },
    selected() { return this.shops.find(s => s.id === this.selectedID) },
    blocked() { return !this.current || this.current.status !== '1' || this.current.daysRemaining < 0 || !this.current.renewTo },
    reminderText() {
      if (!this.current) return ''
      if (this.current.daysRemaining < 0) return '该校区已过期'
      return this.current.daysRemaining === 0 ? '今天是有效期最后一天' : `距离到期还有 ${this.current.daysRemaining} 天`
    }
  },
  watch: {
    shopId() { this.updateAccess(); this.showReminder() },
    employeeId() { this.showReminder() },
    dialog(value) { if (value) this.loadOrders(); else { this.paymentDialog = false; this.stopPolling() } },
    paymentDialog(value) { if (!value) { this.stopPolling(); if (this.dialog) this.loadOrders() } }
  },
  mounted() {
    this.refresh()
    this.timer = setInterval(this.refresh, 5 * 60 * 1000)
    this.clockTimer = setInterval(() => { this.clock = Date.now() }, 1000)
    document.addEventListener('visibilitychange', this.onVisible)
  },
  beforeDestroy() {
    this.disposed = true
    clearInterval(this.timer)
    clearInterval(this.clockTimer)
    this.stopPolling()
    document.removeEventListener('visibilitychange', this.onVisible)
  },
  methods: {
    money(cents) { return (cents / 100).toLocaleString('zh-CN') },
    versionName(level) { return level ? `${level} 版` : '待核实' },
    stateName(order) {
      if (order.state === 'repriced') return '已改价，原二维码失效'
      if (order.state === 'paid') return '已付款并续费'
      if (order.state === 'review') return '待客服核实'
      if (order.state === 'creating') return '确认支付订单中'
      if (order.state === 'closed' || new Date(order.expiresAt).getTime() < Date.now()) return '已失效'
      return order.state === 'draft' ? '已保存，未付款' : '待付款'
    },
    onVisible() { if (!document.hidden) this.refresh() },
    updateAccess() { this.$emit('access', { ready: this.ready, blocked: this.blocked, current: this.current }) },
    async refresh() {
      if (this.loading || this.disposed) return
      this.loading = true
      try {
        const { data } = await this.axios.get('/renewal/status')
        if (data.code !== 200) throw new Error(data.message || '读取续费信息失败')
        if (this.disposed) return
        this.shops = data.data.shops
        this.amountCents = data.data.amountCents
        this.originalCents = data.data.originalCents
        this.channels = data.data.channels || []
        this.ready = true
        this.error = ''
        if (!this.selected) this.selectedID = this.current && this.current.id
        this.updateAccess()
        this.showReminder()
      } catch (e) {
        this.error = e && e.message || '无法连接服务，请稍后重试'
        this.ready = false
        this.updateAccess()
      } finally { this.loading = false }
    },
    reminderKey() {
      return this.current && `campus-renewal:${this.employeeId}:${this.current.id}:${this.current.expireDate}:${this.current.stage}`
    },
    showReminder() {
      if (!this.ready || !this.employeeId || !this.current || !this.current.stage) {
        this.reminder = false
        return
      }
      const key = this.reminderKey()
      let seen = this.dismissed[key]
      try { seen = seen || (this.current.stage !== 'expired' && localStorage.getItem(key)) } catch (_) { /* Storage may be disabled. */ }
      this.reminder = !seen && !this.dialog
    },
    dismissReminder() {
      const key = this.reminderKey()
      if (key) {
        this.dismissed[key] = true
        try { if (this.current.stage !== 'expired') localStorage.setItem(key, '1') } catch (_) { /* Keep session dismissal. */ }
      }
      this.reminder = false
    },
    open() {
      this.dismissReminder()
      this.selectedID = this.current && this.current.id
      this.clearOrder()
      this.historyLoaded = false
      this.historyError = ''
      this.dialog = true
      this.refresh()
    },
    async createOrder() {
      if (!this.selected || this.saving || !this.historyLoaded || this.historyLoading || this.historyError || this.pendingOrders.length || this.reviewOrders.length || !this.ready || this.selected.issue) return
      this.clearOrder()
      this.channel = 'wechat'
      this.paymentDialog = true
      this.saving = true
      this.paymentError = ''
      const generation = this.paymentGeneration
      const shopID = this.selected.id
      const channel = this.channel
      try {
        const { data } = await this.axios.post('/renewal/orders', { shopId: shopID, channel })
        if (data.code !== 200) throw new Error(data.message || '创建续费订单失败')
        if (generation === this.paymentGeneration && this.dialog && this.paymentDialog && this.selectedID === shopID && this.channel === channel) { this.order = data.data; await this.handlePaymentState() }
        await this.loadOrders()
      } catch (e) { if (generation === this.paymentGeneration) this.paymentError = e && e.message || '保存失败，请稍后重试' }
      finally { this.saving = false }
    },
    stopPolling() { clearTimeout(this.pollTimer); this.pollTimer = null; this.paymentGeneration++ },
    clearOrder() { this.paymentDialog = false; this.stopPolling(); this.order = null; this.paymentError = ''; this.completedOrder = null },
    schedulePoll() {
      clearTimeout(this.pollTimer)
      if (!this.disposed && this.dialog && this.paymentDialog && this.order && ['creating', 'pending', 'repriced'].includes(this.order.state)) this.pollTimer = setTimeout(this.pollOrder, 5000)
    },
    async pollOrder() {
      if (!this.order || this.polling || !this.dialog || !this.paymentDialog) return
      clearTimeout(this.pollTimer)
      this.polling = true
      const number = this.order.orderNo
      const generation = this.paymentGeneration
      try {
        const { data } = await this.axios.get('/renewal/orders/' + encodeURIComponent(number))
        if (data.code !== 200) throw new Error(data.message || '查询支付状态失败')
        if (generation !== this.paymentGeneration || !this.order || this.order.orderNo !== number || !this.dialog || !this.paymentDialog) return
        this.order = data.data
        this.paymentError = ''
        await this.handlePaymentState()
      } catch (e) { if (generation === this.paymentGeneration) this.paymentError = e && e.message || '暂时无法确认支付结果，请稍后刷新，勿重复付款' }
      finally { this.polling = false; this.schedulePoll() }
    },
    async handlePaymentState() {
      if (!this.order || !this.paymentDialog) return
      if (this.order.state === 'repriced' && this.order.replacedBy) {
        const oldNumber = this.order.orderNo
        const generation = this.paymentGeneration
        const { data } = await this.axios.post('/renewal/orders/' + encodeURIComponent(this.order.replacedBy) + '/pay', {})
        if (generation !== this.paymentGeneration || !this.dialog || !this.paymentDialog || !this.order || this.order.orderNo !== oldNumber) return
        if (data.code !== 200) throw new Error(data.message || '读取改价后的订单失败，请刷新重试')
        this.order = data.data
        await this.loadOrders()
      }
      if (this.order.state === 'paid' && this.completedOrder !== this.order.orderNo) {
        const number = this.order.orderNo
        const generation = this.paymentGeneration
        try {
          // Refresh staff/campus permissions before remounting expired business views.
          const { data } = await this.axios.get('/sys/employee/myself/info')
          if (this.disposed || generation !== this.paymentGeneration || !this.order || this.order.orderNo !== number) return
          if (data.code !== 200) throw new Error('profile refresh failed')
          global.employee = data.data.employee
          global.routeTree = data.data.menuTree
          const current = storage.getCurrentShop()
          const renewed = (global.employee.shopList || []).find(s => current && s.id === current.id)
          if (renewed) { storage.setCurrentShop(renewed); EventBus.$emit('updateShop') }
          this.completedOrder = number
          await this.refresh()
          await this.loadOrders()
        } catch (e) {
          if (generation === this.paymentGeneration) this.paymentError = '续费已成功，请点击“完成并刷新页面”更新校区权限。'
        }
      }
      this.schedulePoll()
    },
    async openOrder(item) {
      if (this.saving) return
      this.clearOrder()
      this.selectedID = item.shopId
      this.channel = item.channel
      this.order = { ...item }
      this.paymentDialog = true
      this.saving = true
      const generation = this.paymentGeneration
      try {
        if (this.paymentEnabled && ['draft', 'creating', 'pending'].includes(item.state)) {
          const { data } = await this.axios.post('/renewal/orders/' + encodeURIComponent(item.orderNo) + '/pay', {})
          if (data.code !== 200) throw new Error(data.message || '无法继续支付')
          if (generation !== this.paymentGeneration || !this.dialog || !this.paymentDialog || !this.order || this.order.orderNo !== item.orderNo) return
          this.order = data.data
        }
        await this.handlePaymentState()
      } catch (e) { if (generation === this.paymentGeneration) this.paymentError = e && e.message || '暂时无法继续支付' }
      finally { this.saving = false }
    },
    finishPayment() { window.location.reload() },
    async loadOrders() {
      const generation = ++this.historyGeneration
      this.historyLoading = true
      this.historyError = ''
      try {
        const { data } = await this.axios.get('/renewal/orders')
        if (this.disposed || generation !== this.historyGeneration) return
        if (data.code !== 200) throw new Error(data.message || '读取订单失败')
        this.orders = data.data
        this.historyLoaded = true
      } catch (e) { if (generation === this.historyGeneration) { this.historyLoaded = false; this.historyError = e && e.message || '暂时无法读取续费订单' } }
      finally { if (generation === this.historyGeneration) this.historyLoading = false }
    }
  }
}
</script>

<style>
.campus-renewal-dialog {
  --cr-brand: #d9363e;
  --cr-ink: #263247;
  --cr-muted: #647084;
  --cr-line: #e8ecf1;
  --cr-surface: #f7f9fc;
  border-radius: 20px;
}
.campus-renewal-dialog .cr-shell { border-radius: 20px; color: var(--cr-ink); box-shadow: 0 24px 80px rgba(28, 39, 57, .16); }
.campus-renewal-dialog .cr-shell > .cr-header { flex-wrap: nowrap; gap: 14px; padding: 24px 28px 22px; border-bottom: 1px solid var(--cr-line); }
.cr-symbol { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 48px; width: 48px; height: 48px; border-radius: 14px; background: #fff0f0; }
.cr-symbol .v-icon { color: var(--cr-brand); font-size: 26px; }
.cr-symbol-payment { background: #eef8f2; }
.cr-symbol-payment .v-icon { color: #238455; }
.cr-heading { min-width: 0; }
.cr-heading h2 { margin: 0; color: var(--cr-ink); font-size: 21px; line-height: 1.45; font-weight: 700; letter-spacing: 0; }
.cr-heading p { margin: 5px 0 0; color: var(--cr-muted); font-size: 13px; line-height: 1.5; font-weight: 400; letter-spacing: 0; }
.campus-renewal-dialog .cr-close { flex: 0 0 40px; width: 40px; height: 40px; color: var(--cr-muted); }
.campus-renewal-dialog .cr-shell > .cr-body { padding: 24px 28px; color: var(--cr-ink); font-size: 14px; line-height: 1.7; scroll-padding: 20px; }
.campus-renewal-dialog .cr-shell > .cr-footer { gap: 8px; padding: 16px 28px; border-top: 1px solid var(--cr-line); color: var(--cr-muted); background: #fcfcfd; font-size: 13px; flex-wrap: wrap; }
.cr-footer > .v-icon { color: var(--cr-muted); }
.cr-contact { color: #4c596e; font-weight: 500; }
.campus-renewal-dialog .cr-primary { min-height: 42px; padding: 0 22px !important; border-radius: 10px; font-size: 14px; letter-spacing: 0; font-weight: 600; box-shadow: none; }
.campus-renewal-dialog .cr-secondary { min-height: 38px; border-radius: 9px; color: #59667a; letter-spacing: 0; }
.campus-renewal-dialog .v-btn:focus-visible, .campus-renewal-dialog .v-expansion-panel-header:focus-visible { outline: 2px solid var(--cr-brand); outline-offset: 3px; }
.campus-renewal-dialog .v-alert { border-radius: 10px; font-size: 13px; line-height: 1.75; }
.cr-muted { color: var(--cr-muted); font-size: 13px; }
.campus-renewal-dialog .cr-campus-select { margin: 0 0 16px; }
.cr-campus-select .v-input__slot { min-height: 46px !important; border-radius: 10px; }
.cr-campus-select .v-select__selection { max-width: calc(100% - 10px); }
.cr-campus-card { padding: 18px 20px; border: 1px solid var(--cr-line); border-radius: 12px; background: var(--cr-surface); }
.cr-campus-title, .cr-campus-expiry { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.cr-campus-title > strong { min-width: 0; overflow-wrap: anywhere; font-size: 16px; font-weight: 600; }
.cr-version { display: inline-flex; align-items: center; padding: 1px 9px; border: 1px solid #dce3ed; border-radius: 6px; background: #fff; color: #54647a; font-size: 12px; font-weight: 500; line-height: 22px; white-space: nowrap; }
.cr-campus-expiry { justify-content: space-between; margin-top: 14px; color: var(--cr-muted); font-size: 13px; }
.cr-campus-expiry strong { color: var(--cr-ink); font-weight: 600; font-variant-numeric: tabular-nums; }
.cr-status { display: inline-block; padding: 3px 10px; border-radius: 6px; color: #52627a; background: #edf1f7; font-size: 12px; line-height: 20px; font-weight: 500; }
.cr-status-expired { color: #b73940; background: #ffeded; }
.cr-status-pending { color: #966019; background: #fff5e5; }
.cr-status-paid { color: #23764e; background: #eaf7ef; }
.campus-renewal-dialog .cr-expired-note { margin: 10px 0 0; color: #b73940; font-size: 13px; }
.cr-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 22px 0 12px; }
.cr-section-heading h3 { margin: 0; font-size: 15px; font-weight: 600; }
.cr-plan { overflow: hidden; padding: 22px 24px 0; border: 1px solid #efd2d5; border-radius: 14px; background: linear-gradient(115deg, #fff7f6 0%, #fff 80%); }
.cr-plan-top, .cr-plan-pricing, .cr-plan-bottom { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.cr-plan-label { color: var(--cr-muted); font-size: 12px; }
.cr-plan-top h4 { margin: 5px 0 0; font-size: 17px; font-weight: 600; }
.cr-offer-tag { padding: 3px 10px; border-radius: 6px; background: #fce8e8; color: #b62d35; font-size: 12px; line-height: 22px; }
.cr-plan-pricing { justify-content: flex-start; gap: 16px; margin: 16px 0; }
.cr-price { display: flex; align-items: baseline; flex-wrap: wrap; gap: 3px; color: var(--cr-brand); line-height: 1.2; }
.cr-currency { font-size: 23px; font-weight: 600; }
.cr-price strong { font-size: 40px; font-weight: 700; letter-spacing: -1px; font-variant-numeric: tabular-nums; }
.cr-price-unit { margin-left: 8px; color: var(--cr-muted); font-size: 13px; font-weight: 400; letter-spacing: 0; }
.cr-original-price { color: var(--cr-muted); font-size: 13px; }
.cr-benefits { display: flex; flex-wrap: wrap; gap: 10px 22px; padding-bottom: 20px; color: #5f6b7c; font-size: 13px; }
.cr-benefits span { display: inline-flex; align-items: center; gap: 6px; }
.cr-benefits .v-icon { color: #b95258; }
.cr-plan-bottom { margin: 0 -24px; padding: 16px 24px; border-top: 1px solid #f0e3e5; background: rgba(255, 255, 255, .6); color: var(--cr-muted); font-size: 13px; }
.cr-rule { display: flex; align-items: flex-start; gap: 7px; margin-top: 14px; color: var(--cr-muted); font-size: 12px; line-height: 1.7; }
.cr-rule .v-icon { flex-shrink: 0; margin-top: 2px; color: var(--cr-muted); }
.campus-renewal-dialog .cr-offline-note { margin: 16px 0 0; }
.cr-history { margin-top: 20px; border: 1px solid var(--cr-line); border-radius: 10px; overflow: hidden; }
.cr-history .v-expansion-panel-header { min-height: 52px; padding: 14px 16px; color: #5f6b7c; font-size: 13px; }
.cr-history .v-expansion-panel-content__wrap { padding: 0 12px 12px; }
.cr-history .cr-history-item { padding: 8px; }
.cr-history .v-list-item__title { color: var(--cr-ink); font-size: 13px; font-weight: 500; }
.cr-history .v-list-item__subtitle { color: var(--cr-muted); font-size: 12px; }
.campus-renewal-dialog .cr-pending-hint { display: flex; align-items: center; gap: 7px; margin-bottom: 12px; color: var(--cr-muted); font-size: 13px; }
.cr-pending-hint .v-icon { color: #9b6c2f; }
.cr-pending-card { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 12px; padding: 20px; border: 1px solid #e6d5b9; border-radius: 12px; background: #fffdf8; }
.cr-pending-main { min-width: 0; }
.cr-pending-main h4 { margin: 10px 0 4px; color: var(--cr-ink); font-size: 15px; font-weight: 600; }
.cr-order-number { color: var(--cr-muted); font-size: 12px; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.cr-pending-action { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; gap: 12px; }
.cr-pending-price { color: var(--cr-brand); font-size: 26px; font-weight: 600; font-variant-numeric: tabular-nums; }
.cr-payment-summary { padding-bottom: 22px; text-align: center; border-bottom: 1px dashed #dde3eb; }
.cr-payment-campus { display: flex; justify-content: center; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
.cr-payment-plan { margin: 8px 0 12px; color: var(--cr-muted); font-size: 13px; }
.cr-payment-amount { display: flex; justify-content: center; align-items: baseline; flex-wrap: wrap; gap: 2px; }
.cr-payment-amount .text-h4 { color: var(--cr-brand); font-family: inherit !important; font-size: 38px !important; font-weight: 700; line-height: 1.3; font-variant-numeric: tabular-nums; }
.cr-payment-expiry { margin-top: 12px; color: var(--cr-muted); font-size: 13px; }
.cr-payment-expiry strong { color: var(--cr-ink); font-weight: 600; }
.cr-payment-detail { padding-top: 20px; text-align: center; }
.cr-payment-status { margin-bottom: 10px; }
.cr-payment-caption { margin-top: 5px; color: var(--cr-muted); font-size: 13px; }
.cr-qr-area { margin-top: 18px; }
.cr-qr-frame { display: inline-flex; padding: 10px; border: 1px solid #e3e9e6; border-radius: 14px; background: #fff; box-shadow: 0 5px 18px rgba(32, 75, 50, .04); }
.cr-qr-frame img { display: block; width: 216px; height: 216px; }
.campus-renewal-dialog .cr-scan-instruction { display: flex; align-items: center; justify-content: center; gap: 7px; margin: 14px 0 6px; color: #287d50; font-weight: 500; }
.cr-scan-instruction .v-icon { color: #287d50; }
.campus-renewal-dialog .cr-countdown { margin: 0; color: var(--cr-muted); font-size: 12px; }
.cr-countdown strong { color: #866022; font-weight: 500; font-variant-numeric: tabular-nums; }
.campus-renewal-dialog .cr-payment-rule { margin: 18px 0 0; color: var(--cr-muted); font-size: 12px; text-align: center; line-height: 1.8; }
.cr-reminder-campus { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 16px; }
.cr-reminder-campus strong { overflow-wrap: anywhere; }
.cr-reminder-campus .v-icon { color: var(--cr-muted); }
.campus-renewal-dialog .cr-reminder-message { margin-bottom: 20px; color: var(--cr-muted); }
.cr-reminder-message strong { color: var(--cr-ink); font-weight: 600; }
.cr-reminder-offer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; padding: 16px; border-radius: 12px; background: var(--cr-surface); }
.cr-reminder-offer > div:first-child { display: flex; flex-direction: column; gap: 5px; }
.cr-eyebrow { color: var(--cr-ink); font-size: 14px; font-weight: 600; }
.cr-mini-price { color: var(--cr-brand); font-size: 25px; font-weight: 700; }
.cr-mini-price > span { color: var(--cr-muted); font-size: 12px; font-weight: 400; }
@media (max-width: 600px) {
  .campus-renewal-dialog.v-dialog { margin: 12px; max-height: calc(100% - 24px); border-radius: 16px; }
  .campus-renewal-dialog .cr-shell { border-radius: 16px; }
  .campus-renewal-dialog .cr-shell > .cr-header { gap: 10px; padding: 18px; }
  .cr-symbol { flex-basis: 40px; width: 40px; height: 40px; border-radius: 12px; }
  .cr-symbol .v-icon { font-size: 23px; }
  .cr-heading h2 { font-size: 19px; }
  .cr-heading p { font-size: 12px; }
  .campus-renewal-dialog .cr-shell > .cr-body { padding: 18px; }
  .campus-renewal-dialog .cr-shell > .cr-footer { padding: 14px 18px; }
  .cr-campus-card { padding: 16px; }
  .cr-plan { padding: 18px 18px 0; }
  .cr-plan-top { align-items: flex-start; gap: 8px; }
  .cr-plan-top h4 { font-size: 16px; }
  .cr-price strong { font-size: 36px; }
  .cr-plan-bottom { margin: 0 -18px; padding: 14px 18px; }
  .cr-plan-bottom > span { width: 100%; }
  .cr-plan-bottom .cr-primary { width: 100%; }
  .cr-pending-card { align-items: flex-start; flex-direction: column; padding: 16px; gap: 12px; }
  .cr-pending-action { width: 100%; flex-direction: row; align-items: center; justify-content: space-between; }
  .cr-payment-footer .cr-contact { width: 100%; }
  .cr-qr-frame img { width: 200px; height: 200px; }
}
@media (max-height: 600px) {
  .campus-renewal-dialog.v-dialog--scrollable { display: block; overflow-y: auto; max-height: calc(100% - 24px); margin: 12px; }
  .campus-renewal-dialog.v-dialog--scrollable > .cr-shell { max-height: none; }
  .campus-renewal-dialog .cr-shell > .cr-body { overflow-y: visible; flex: 0 0 auto; }
}
</style>
